import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveRefresh } from "@/contexts/LiveRefresh";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Edit2, Plus, Trash2, Loader2, ShieldCheck, UserCog } from "lucide-react";
import UserFormDialog from "@/components/UserFormDialog";
import { toast } from "sonner";

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [toDelete, setToDelete] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);
    useLiveRefresh(load);

    const openNew = () => {
        setEditing(null);
        setShowForm(true);
    };

    const openEdit = (u) => {
        setEditing(u);
        setShowForm(true);
    };

    const toggleActive = async (u, value) => {
        try {
            await api.patch(`/users/${u.id}`, { is_active: value });
            toast.success(value ? "User activated" : "User disabled");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const onDelete = async () => {
        if (!toDelete) return;
        try {
            await api.delete(`/users/${toDelete.id}`);
            toast.success("User deleted");
            setToDelete(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="users-page">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Access control
                    </div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                        Users
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Add team members by email and grant them access. Only admins can manage users.
                    </p>
                </div>
                <Button onClick={openNew} data-testid="new-user-button">
                    <Plus className="mr-1.5 h-4 w-4" />
                    Add user
                </Button>
            </div>

            <Card className="sdc-card overflow-hidden">
                {loading ? (
                    <div className="grid place-items-center py-20">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Designation</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>Last login</TableHead>
                                    <TableHead>Active</TableHead>
                                    <TableHead className="text-right pr-6">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.map((u) => (
                                    <TableRow
                                        key={u.id}
                                        className="sdc-table-row"
                                        data-testid={`user-row-${u.id}`}
                                    >
                                        <TableCell className="font-semibold">
                                            {u.name}
                                            {u.id === me?.id && (
                                                <span className="ml-2 sdc-pill border-primary/20 bg-primary/5 text-primary">
                                                    You
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm">{u.email}</TableCell>
                                        <TableCell>
                                            {u.role === "admin" ? (
                                                <span className="sdc-pill border-primary/20 bg-primary/5 text-primary">
                                                    <ShieldCheck className="h-3 w-3" /> Admin
                                                </span>
                                            ) : (
                                                <span className="sdc-pill border-border bg-muted text-muted-foreground">
                                                    <UserCog className="h-3 w-3" /> Member
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {u.designation || "—"}
                                        </TableCell>
                                        <TableCell className="text-sm">{u.phone || "—"}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {u.last_login
                                                ? new Date(u.last_login).toLocaleString("en-IN")
                                                : "Never"}
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={u.is_active}
                                                disabled={u.id === me?.id}
                                                onCheckedChange={(v) => toggleActive(u, v)}
                                                data-testid={`user-toggle-${u.id}`}
                                            />
                                        </TableCell>
                                        <TableCell className="text-right pr-4">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => openEdit(u)}
                                                    data-testid={`user-edit-${u.id}`}
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => setToDelete(u)}
                                                    disabled={u.id === me?.id}
                                                    data-testid={`user-delete-${u.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-600" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            <UserFormDialog
                open={showForm}
                onOpenChange={setShowForm}
                userToEdit={editing}
                onSaved={() => load()}
            />

            <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this user?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Their account will be permanently removed. Audit log entries are kept.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
